import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { categories, movies } from "@/mocks/movies";
import { paginate, searchMovies, sortMovies } from "@/utils/movies";
import { useSEO } from "@/hooks/useSEO";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import Pagination from "@/components/base/Pagination";
import SearchBar from "@/components/base/SearchBar";
import SectionHeader from "@/components/base/SectionHeader";

const filterTabs = [{ key: "all", name: "全部" }, ...categories];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") ?? "";
  const category = params.get("cat") ?? "all";
  const rawPage = Number(params.get("page") ?? "1");
  const page = Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1;

  useSEO(
    keyword ? `${keyword} - 搜索 - 色虎视频` : "影视搜索 - 色虎视频",
    `在色虎视频搜索「${keyword || "影视资源"}」，快速找到相关电影、电视剧、综艺与动漫内容。`,
  );

  const updateParams = (next: Record<string, string | undefined>) => {
    const merged = new URLSearchParams(params);
    Object.entries(next).forEach(([key, value]) => {
      if (!value) merged.delete(key);
      else merged.set(key, value);
    });
    setParams(merged, { replace: true });
  };

  const results = useMemo(
    () => searchMovies(movies, keyword, category),
    [keyword, category],
  );
  const { items, total, totalPages, page: safePage } = paginate(results, page);
  const hotMovies = useMemo(() => sortMovies(movies, "hot").slice(0, 5), []);

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索结果" }]} />

      <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
        <h1 className="font-heading text-xl font-extrabold tracking-tight text-foreground-950 md:text-2xl">
          {keyword ? `“${keyword}” 的搜索结果` : "站内搜索"}
        </h1>
        <p className="mt-1.5 text-sm text-foreground-500">
          {keyword ? "以下是与关键词匹配的影视资源" : "输入片名、主演、导演或类型关键词开始搜索"}
        </p>
        <div className="mt-4 max-w-2xl">
          <SearchBar
            key={keyword}
            defaultValue={keyword}
            size="lg"
            onSearch={(value) => updateParams({ q: value || undefined, page: undefined })}
          />
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          {filterTabs.map((tab) => {
            const active = tab.key === category;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() =>
                  updateParams({ cat: tab.key === "all" ? undefined : tab.key, page: undefined })
                }
                className={`cursor-pointer whitespace-nowrap rounded-full border px-4 py-1.5 text-sm transition-colors ${
                  active
                    ? "border-primary-500 bg-primary-500 text-background-50"
                    : "border-background-200 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                {tab.name}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-foreground-500">
          共找到 <strong className="font-label font-bold text-accent-600">{total}</strong> 部相关作品
        </p>
      </div>

      {items.length > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
            {items.map((movie, index) => (
              <MovieCard key={movie.id} movie={movie} index={index} />
            ))}
          </div>
          <Pagination
            page={safePage}
            totalPages={totalPages}
            onChange={(nextPage) => {
              updateParams({ page: nextPage <= 1 ? undefined : String(nextPage) });
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        </>
      ) : (
        <div className="mt-6">
          <div className="flex flex-col items-center justify-center rounded-lg border border-background-200 bg-background-50 px-6 py-14 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-eye-line text-3xl" aria-hidden="true" />
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-900">
              没有找到相关影视资源
            </h2>
            <p className="mt-2 max-w-md text-sm text-foreground-500">
              换个关键词试试，或浏览下方热门推荐，也许会有意外收获。
            </p>
          </div>

          <div className="mt-10">
            <SectionHeader title="热门影视推荐" subtitle="大家都在看的作品" moreHref="/category/movie" />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
              {hotMovies.map((movie, index) => (
                <MovieCard key={movie.id} movie={movie} index={index} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}